import { contextUsagePercent, formatContextUsage } from "@/lib/context-usage";
import { useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import { useCompanionStore } from "@/stores/companion-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import type { InputBarFocusedAgent } from "./input-bar-model-pill";
import type { InputBarConversationController } from "./input-bar-types";

/** The same chat/session matching rules for the default and standalone rings. */
export function useInputBarContextUsage({
  focusedAgent,
  conversationController,
  workerUsage,
}: {
  focusedAgent: InputBarFocusedAgent | null;
  conversationController?: InputBarConversationController;
  workerUsage?: { used: number; max: number } | null;
}) {
  const t = useT(inputBarMessages);
  const globalTarget = useInputBarTarget((state) => state.target);
  const globalStartNewConversation = useInputBarTarget(
    (state) => state.startNewConversation,
  );
  const target = conversationController
    ? conversationController.target
    : globalTarget;
  const startNewConversation = conversationController
    ? conversationController.startNewConversation
    : globalStartNewConversation;
  const conversationId = conversationController
    ? conversationController.conversationId
    : (target?.conversationId ?? focusedAgent?.conversationId);
  const matchesFocusedAgent =
    !target || target.projectAgentId === focusedAgent?.id;
  const matchingTarget = matchesFocusedAgent ? target : null;
  const hasCurrentConversation =
    !startNewConversation && Boolean(conversationId) && matchesFocusedAgent;
  const workerTarget = workerUsage !== undefined;
  const tokenKey =
    !workerTarget && focusedAgent?.agentName && focusedAgent?.spaceId
      ? `${focusedAgent?.agentName.toLowerCase()}:${focusedAgent?.spaceId}`
      : null;
  const activeSession = useCompanionStore((state) =>
    tokenKey ? state.agentSessions[tokenKey] : undefined,
  );
  const selectedRuntimeSessionId =
    target?.runtimeSessionId ?? focusedAgent?.runtimeSessionId;
  const activeSessionMatches = Boolean(
    activeSession &&
    (!selectedRuntimeSessionId ||
      activeSession.runtimeSessionId === selectedRuntimeSessionId) &&
    (!conversationId ||
      !activeSession.conversationId ||
      activeSession.conversationId === conversationId),
  );
  const storedSessionTokens = useCompanionStore((state) =>
    tokenKey ? state.sessionTokens[tokenKey] : undefined,
  );
  const tokenRuntimeMatches = Boolean(
    selectedRuntimeSessionId &&
    storedSessionTokens?.runtimeSessionId === selectedRuntimeSessionId,
  );
  const tokenConversationMatches = Boolean(
    conversationId && storedSessionTokens?.conversationId === conversationId,
  );
  const tokenConversationConflicts = Boolean(
    conversationId &&
    storedSessionTokens?.conversationId &&
    storedSessionTokens.conversationId !== conversationId,
  );
  const tokenRuntimeConflicts = Boolean(
    selectedRuntimeSessionId &&
    storedSessionTokens?.runtimeSessionId &&
    storedSessionTokens.runtimeSessionId !== selectedRuntimeSessionId,
  );
  const legacyTokenMatches = Boolean(
    activeSessionMatches &&
    !storedSessionTokens?.runtimeSessionId &&
    !storedSessionTokens?.conversationId,
  );
  const sessionTokens =
    hasCurrentConversation &&
    !tokenConversationConflicts &&
    !tokenRuntimeConflicts &&
    (tokenRuntimeMatches || tokenConversationMatches || legacyTokenMatches)
      ? storedSessionTokens
      : undefined;
  const persistedContextPercent = matchingTarget
    ? matchingTarget.persistedContextPercent
    : focusedAgent?.persistedContextPercent;
  const persistedContextUsedTokens = matchingTarget
    ? matchingTarget.persistedContextUsedTokens
    : focusedAgent?.persistedContextUsedTokens;
  const persistedContextWindowMax = matchingTarget
    ? matchingTarget.persistedContextWindowMax
    : focusedAgent?.persistedContextWindowMax;
  const contextPercent = hasCurrentConversation
    ? sessionTokens
      ? (focusedAgent?.contextPercent ?? persistedContextPercent ?? null)
      : (persistedContextPercent ?? null)
    : 0;
  const contextWindowMax = sessionTokens
    ? sessionTokens.max
    : persistedContextWindowMax;
  const contextUsed = sessionTokens
    ? sessionTokens.contextUsed
    : persistedContextUsedTokens;
  const tokenPercent = hasCurrentConversation
    ? contextUsagePercent({
        used: contextUsed,
        max: contextWindowMax,
        fallbackPercent: contextPercent,
      })
    : 0;
  const contextTitle = startNewConversation
    ? t("newChatSelected")
    : hasCurrentConversation
      ? tokenPercent === null || contextUsed == null || contextWindowMax == null
        ? t("contextUnavailableInChat")
        : formatContextUsage(contextUsed, contextWindowMax)
      : t("currentChatContext");
  const ringPercent = workerTarget
    ? workerUsage
      ? (contextUsagePercent(workerUsage) ?? 0)
      : 0
    : tokenPercent;
  const menuTitle = workerTarget
    ? workerUsage
      ? formatContextUsage(workerUsage.used, workerUsage.max)
      : t("contextUnavailable")
    : contextTitle;
  return {
    globalTarget,
    startNewConversation,
    conversationId,
    hasCurrentConversation,
    workerTarget,
    tokenPercent,
    ringPercent,
    menuTitle,
    contextUsed,
    contextWindowMax,
  };
}
